import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { addDays, WEEKDAY_SHORT } from '@shh/shared';
import { cn, IconButton, TextField } from '@shh/ui';
import { useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { addMonths, calendarDay, weekStart } from '../../data/calendar';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';
import { locale } from '../../lib/locale';
import { TodayTodos } from './CompactLists';
import { dayNumber, dayTitle, monthTitle } from './format';

/**
 * Left column on tablets and computers (1.24): a small month to jump and today's to-dos as a
 * compact list. It folds in and out (remembered per device) – closed on tablets at first,
 * Mike 19.10.: "Spendiere der Monats- und Wochenansicht mehr Platz!". The next seven days live
 * beside the day view only ("die 7-Tages-Vorschau braucht es nur in der Tagesansicht"), and the
 * day view shows the to-dos itself, so the sidebar leaves them out there.
 */
export function CalendarSidebar({
  f,
  date,
  range,
  people,
  query,
  onQuery,
  onPick,
  showTodos,
}: {
  f: FamilyData;
  date: string;
  /** The days the main view shows – lit in the small month. */
  range: [string, string] | null;
  people?: string[];
  query: string;
  onQuery: (q: string) => void;
  onPick: (date: string) => void;
  showTodos: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const first = addMonths(`${date.slice(0, 8)}01`, offset);
  const start = weekStart(first);
  const next = addMonths(first, 1);
  const cells: string[] = [];
  for (let d = start; d < next || cells.length % 7; d = addDays(d, 1)) cells.push(d);

  return (
    <aside aria-label={calendarText.views} className="flex flex-col gap-section">
      <TextField label={calendarText.searchLabel} value={query} onChange={onQuery} type="search" />

      <section aria-label={monthTitle(first)} className="flex flex-col gap-gap-xs">
        <div className="flex items-center justify-between">
          <span className="font-black">{monthTitle(first)}</span>
          <span className="flex">
            <IconButton
              icon={CaretLeft}
              aria-label={calendarText.back}
              onPress={() => setOffset((o) => o - 1)}
            />
            <IconButton
              icon={CaretRight}
              aria-label={calendarText.forward}
              onPress={() => setOffset((o) => o + 1)}
            />
          </span>
        </div>
        <div className="grid grid-cols-7 text-center text-sm">
          {WEEKDAY_SHORT[locale].map((w) => (
            <span key={w} aria-hidden className="py-1 text-xs font-black text-text-secondary">
              {w.slice(0, 1)}
            </span>
          ))}
          {cells.map((d) => {
            const inRange = range && d >= range[0] && d <= range[1];
            const hol = calendarDay(f, d, { people }).some(
              (e) => e.virtual === 'holiday' && !e.publicHoliday,
            );
            return (
              <AriaButton
                key={d}
                aria-label={dayTitle(d)}
                aria-current={d === f.today ? 'date' : undefined}
                onPress={() => {
                  setOffset(0);
                  onPick(d);
                }}
                className={cn(
                  'relative min-h-touch-min cursor-pointer font-semibold tabular-nums outline-none data-hovered:bg-action-quiet-hover data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                  d.slice(0, 7) !== first.slice(0, 7) && 'font-medium text-text-muted',
                  inRange && 'bg-accent-secondary-soft',
                  d === f.today && 'font-black text-feedback-danger',
                  d === date && 'rounded-control bg-text-primary text-surface-canvas',
                )}
              >
                {dayNumber(d)}
                {hol && (
                  <span
                    aria-hidden
                    className="absolute inset-x-1 bottom-1 h-1 rounded-chip bg-feedback-warning-surface"
                  />
                )}
              </AriaButton>
            );
          })}
        </div>
      </section>

      {showTodos && <TodayTodos f={f} people={people} />}
    </aside>
  );
}
